/* eslint-disable @typescript-eslint/no-explicit-any */
// src/stores/sync.ts (수정된 버전)

import { Network } from '@capacitor/network'
import { getRandomString, getUnixtime } from '@/lib/Functions'
import { DependencyNotReadyError, DependencyRejectedError } from './syncError'
import { isSyncSuspended } from './suspend'
import { SQL } from '../../lib/Sqlite'
import { parseRow } from '@/app/util'
import { UserExportReportRecord } from '../data/type'
import {
  Defect,
  DefectPhoto,
  FacilityPhoto,
  Floorplan,
  QualityInspection,
  QualityInspectionResult,
  QualityResultPhoto,
} from '../data/types'
import { ApiError } from '../auth'
import Merge from '../../lib/Task/merge'
import { isSyncRejectedActive, isValidationRejectStatus } from './syncRejection'

// 작업 상태 정의 (변경 없음)
export enum JobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

// 작업 우선순위 정의 (변경 없음)
export enum JobPriority {
  HIGH = 0,
  NORMAL = 1,
  LOW = 2,
  VERY_LOW = 3,
}

// 큐 크기 제한 및 만료 설정
const MAX_QUEUE_SIZE = 500
const MAX_FAILED_JOB_AGE_MS = 30 * 60 * 1000 // 30분
const RETRY_SWEEP_INTERVAL_MS = 30 * 1000 // 30초
// pause 최대 유지 시간 - 초과 시 retry sweep이 강제 재개 (resume 유실 안전망)
const MAX_PAUSE_DURATION_MS = 10 * 60 * 1000 // 10분

// 작업 객체 인터페이스 (변경 없음)
export interface SyncJob<T = any> {
  id: string
  localStringId?: string
  priority: JobPriority
  data: T
  tag?: string

  execute: (data: T) => Promise<any>
  onSuccess: (result: any, data: T) => void | Promise<void>
  onError: (error: any, data: T, errorCode?: number) => void | Promise<void>
  onIdMap?: (localStringId: string, serverResult: any) => void | Promise<void>
  status: JobStatus
  retryCount: number
  maxRetries: number
  createdAt: number
  updatedAt: number
  error?: string
  // backoff 재시도 예정 시각 (ms timestamp)
  nextRetryAt?: number
}

/**
 * 병합 soft-delete 유예 대상(이 job이 서버에 새로 등장시킬 항목)이면 localStringId를 돌려준다.
 * 서버 미생성 항목의 생성(id `_need_to_upload`)과 삭제 항목의 복원(need_restore)만 해당 —
 * 이미 서버에 있던 항목의 수정은 서버 GET에서 빠질 수 없으므로 유예하지 않는다.
 */
function getUploadGraceKey(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const row = data as Record<string, unknown>
  if (row.deleted_at || row.localStringId === undefined || row.localStringId === null) return null
  if (String(row.id).includes('_need_to_upload')) return String(row.localStringId)
  let fieldUpdates: unknown = row.fields_updated_at
  if (typeof fieldUpdates === 'string') {
    try {
      fieldUpdates = JSON.parse(fieldUpdates)
    } catch {
      fieldUpdates = null
    }
  }
  const needRestore = (fieldUpdates as Record<string, unknown> | null)?.need_restore
  return needRestore ? String(row.localStringId) : null
}

/**
 * 동기화 작업을 관리하는 클래스 (콜백 기반)
 */
export class SyncQueue {
  private static instance: SyncQueue
  private queue: SyncJob[] = []
  isProcessing = false
  private isNetworkConnected = false
  // 카메라 촬영 등 메모리 민감 구간 동안 큐 처리를 멈추는 참조 카운트
  // (동시 pause 호출 시 마지막 resume까지 정지 상태를 유지하기 위해 boolean 대신 카운트 사용)
  private pauseCount = 0
  // 일시정지 시작 시각 (장시간 정지 시 강제 재개 판정용)
  private pausedAt = 0
  // 같은 localStringId에 대한 대기 큐
  private pendingQueues: Map<string, SyncJob[]> = new Map()
  // 부모 결함 거절로 의존 job(사진·자식 결함)을 끝내거나 생략한 부모 localStringId
  private blockedDependencies: Set<string> = new Set()
  // 큐 상태 변경 리스너
  private queueChangeListeners: Array<() => void> = []
  // processQueue 락 (race condition 방지)
  private processLock: Promise<void> | null = null
  private processLockResolve: (() => void) | null = null
  // retry sweep 타이머
  private retrySweepTimer: ReturnType<typeof setInterval> | null = null

  public static getInstance(): SyncQueue {
    if (!SyncQueue.instance) {
      SyncQueue.instance = new SyncQueue()
    }
    return SyncQueue.instance
  }

  private constructor() {
    this.setupNetworkListener()
    this.startRetrySweep()
  }

  // 주기적으로 FAILED 작업을 검사하여 재시도 또는 만료 처리
  private startRetrySweep(): void {
    this.retrySweepTimer = setInterval(() => {
      const now = Date.now()

      // pause 안전망: 네이티브 콜백 유실 등으로 resume이 호출되지 못해
      // 큐가 영구 정지되는 것을 방지 (카메라 촬영은 이 시간을 넘지 않는다고 가정)
      if (this.pauseCount > 0 && now - this.pausedAt > MAX_PAUSE_DURATION_MS) {
        console.warn('[SyncQueue] pause가 장시간 유지되어 강제 재개합니다.')
        this.pauseCount = 0
        this.processQueue()
      }

      let promoted = 0
      let purged = 0

      this.queue = this.queue.filter(job => {
        // FAILED + maxRetries 초과한 작업만 대상
        if (job.retryCount < job.maxRetries || job.status === JobStatus.COMPLETED) return true

        // 30분 초과 체류한 FAILED 작업 제거
        const jobAge = now - job.updatedAt * 1000
        if (jobAge > MAX_FAILED_JOB_AGE_MS) {
          console.warn(
            `[SyncQueue] Purging expired FAILED job ${job.id} (${job.localStringId}, age: ${Math.round(jobAge / 1000 / 60)}min)`,
          )
          purged++
          return false
        }
        return true
      })

      // nextRetryAt이 경과한 FAILED 작업을 PENDING으로 전환
      for (const job of this.queue) {
        if (job.status === JobStatus.FAILED && job.nextRetryAt && job.nextRetryAt <= now) {
          job.status = JobStatus.PENDING
          job.nextRetryAt = undefined
          job.updatedAt = getUnixtime()
          promoted++
        }
      }

      if (purged > 0 || promoted > 0) {
        console.info(
          `[SyncQueue] Retry sweep: promoted=${promoted}, purged=${purged}, queueSize=${this.queue.length}`,
        )
        this.notifyQueueChange()
        if (promoted > 0) {
          this.processQueue()
        }
      }

      // 네트워크 플래핑 등으로 처리 킥을 놓친 PENDING 작업 안전망
      // isNetworkConnected 캐시는 connected 이벤트가 오지 않으면 false로 고착될 수 있으므로
      // 실제 상태를 재조회해 플래그를 보정한 뒤 재개한다
      if (this.queue.some(job => job.status === JobStatus.PENDING)) {
        Network.getStatus()
          .then(status => {
            this.isNetworkConnected = status.connected
            if (status.connected) {
              this.processQueue()
            }
          })
          .catch(() => {
            // 상태 조회 실패 시 다음 sweep에서 재시도
          })
      }
    }, RETRY_SWEEP_INTERVAL_MS)
  }

  /**
   * FAILED 작업을 즉시 PENDING으로 깨워 재시도한다 (30초 retry sweep을 기다리지 않음).
   * 의존성 해소 시점(예: 결함 업로드 완료 → 대기 중인 사진 job)에 호출해
   * "n건 저장중" 배지가 sweep 주기만큼 지연되는 것을 방지한다.
   * @param tag 특정 tag의 job만 깨우기 (생략 시 전체 FAILED)
   */
  public wakeFailedJobs(tag?: string): void {
    const now = Date.now()
    let promoted = 0
    for (const job of this.queue) {
      if (job.status !== JobStatus.FAILED) continue
      // FAILED는 retryCount >= maxRetries일 때만 부여되므로 여기서 걸러내면 전부 스킵된다.
      // 대신 30분 초과 체류한(만료 대상) job만 제외한다.
      if (now - job.updatedAt * 1000 > MAX_FAILED_JOB_AGE_MS) continue
      if (tag && job.tag !== tag) continue
      job.status = JobStatus.PENDING
      job.nextRetryAt = undefined
      job.updatedAt = getUnixtime()
      promoted++
    }
    if (promoted > 0) {
      console.info(`[SyncQueue] wakeFailedJobs: ${promoted}건 즉시 재시도 (tag=${tag ?? 'all'})`)
      this.notifyQueueChange()
      this.processQueue()
    }
  }

  // 큐 크기 초과 시 오래된 FAILED 작업부터 제거
  private enforceQueueSizeLimit(): void {
    if (this.queue.length <= MAX_QUEUE_SIZE) return

    const failedJobs = this.queue
      .filter(j => j.status === JobStatus.FAILED)
      .sort((a, b) => a.updatedAt - b.updatedAt)

    let removed = 0
    const idsToRemove = new Set<string>()
    for (const job of failedJobs) {
      if (this.queue.length - removed <= MAX_QUEUE_SIZE) break
      idsToRemove.add(job.id)
      removed++
    }

    if (removed > 0) {
      this.queue = this.queue.filter(j => !idsToRemove.has(j.id))
      console.warn(
        `[SyncQueue] Queue size limit enforced: removed ${removed} oldest FAILED jobs. Current size: ${this.queue.length}`,
      )
    }
  }

  private async setupNetworkListener(): Promise<void> {
    const status = await Network.getStatus()
    this.isNetworkConnected = status.connected
    Network.addListener('networkStatusChange', status => {
      const wasConnected = this.isNetworkConnected
      this.isNetworkConnected = status.connected
      if (!this.isNetworkConnected) return
      // 재연결 전환뿐 아니라 connected 반복 이벤트에서도 PENDING이 있으면 처리 재개
      // (addJob 시점에 순간 오프라인이었던 job이 다음 킥 없이 방치되는 것을 방지 -
      //  processQueue는 PENDING이 없으면 즉시 반환하므로 자주 호출해도 저비용)
      if (!wasConnected || this.queue.some(job => job.status === JobStatus.PENDING)) {
        this.processQueue()
      }
    })
    if (this.isNetworkConnected) {
      this.processQueue()
    }
  }

  /**
   * 작업을 추가하거나, 동일한 localStringId를 가진 기존 작업을 업데이트합니다. (Upsert 로직)
   * @param jobDetails 추가 또는 업데이트할 작업의 상세 정보
   * @returns 작업 ID
   */
  public addJob<T>(
    jobDetails: Omit<SyncJob<T>, 'id' | 'status' | 'retryCount' | 'createdAt' | 'updatedAt'>,
  ): string {
    const jobIdentifier = jobDetails.localStringId || `job_${getRandomString(8, false)}`
    const tag = jobDetails.tag || 'unknown'

    // 배치 작업(-batch 접미사)은 중복 체크를 하지 않고 항상 새로 추가
    if (tag.endsWith('-batch')) {
      console.info(`[SyncQueue] ➕ INSERT: Adding new batch job ${jobIdentifier} (${tag})`)
      const newJob: SyncJob<T> = {
        id: `job_${getRandomString(8, false)}`,
        status: JobStatus.PENDING,
        retryCount: 0,
        createdAt: getUnixtime(),
        updatedAt: getUnixtime(),
        ...jobDetails,
        localStringId: jobIdentifier,
      }
      this.queue.push(newJob)
      this.notifyQueueChange()
      this.processQueue()
      return newJob.id
    }

    // 1. 동일한 localStringId를 가진 작업이 "진행 중(PROCESSING)" 또는 "대기 중(PENDING)"인지 확인합니다.
    const existingJobIndex = this.queue.findIndex(
      j => j.localStringId === jobIdentifier && j.status !== JobStatus.COMPLETED, // 👈 완료되지 않은 모든 job 체크
    )

    // 2. 기존 작업이 있는 경우
    if (existingJobIndex !== -1) {
      const existingJob = this.queue[existingJobIndex]

      // 2a. 만약 기존 작업이 이미 "진행 중(PROCESSING)"이라면, 대기 큐에 추가를 고려합니다.
      if (existingJob.status === JobStatus.PROCESSING) {
        // pendingQueues에 이미 같은 작업이 있는지 확인
        const existingPendingJobs = this.pendingQueues.get(jobIdentifier) || []

        // signature가 있는 경우에만 체크 (log-batch는 이미 위에서 처리됨)
        if (tag !== 'log-batch') {
          const executionSignature = this.generateExecutionSignature(jobDetails.data, tag)
          const hasSameSignature = existingPendingJobs.some(pJob => {
            const pSignature = this.generateExecutionSignature(pJob.data, pJob.tag)
            return pSignature === executionSignature
          })

          if (hasSameSignature) {
            console.warn(
              `[SyncQueue] ⏭️  SKIP: ${jobIdentifier} (${tag}) - same signature already in pendingQueues`,
            )
            return existingPendingJobs[0].id
          }
        }

        console.info(
          `[SyncQueue] ⏸️  Job for ${jobIdentifier} (${tag}) is PROCESSING. Adding to pending queue.`,
        )

        // 대기 큐에 새 작업 추가
        const newJob: SyncJob<T> = {
          id: `job_${getRandomString(8, false)}`,
          status: JobStatus.PENDING,
          retryCount: 0,
          createdAt: getUnixtime(),
          updatedAt: getUnixtime(),
          ...jobDetails,
          localStringId: jobIdentifier,
        }

        if (!this.pendingQueues.has(jobIdentifier)) {
          this.pendingQueues.set(jobIdentifier, [])
        }

        this.pendingQueues.get(jobIdentifier)!.push(newJob)

        console.info(
          `[SyncQueue] 📋 pendingQueues for ${jobIdentifier} now has ${this.pendingQueues.get(jobIdentifier)!.length} job(s)`,
        )

        return newJob.id
      }

      // 2b. 기존 작업이 "대기 중(PENDING)"이라면, 최신 데이터로 업데이트합니다.
      console.info(
        `[SyncQueue] 🔄 UPSERT: Updating existing PENDING job ${existingJob.id} for ${jobIdentifier} (${tag})`,
      )
      this.queue[existingJobIndex] = {
        ...existingJob,
        ...jobDetails,
        localStringId: jobIdentifier,
        updatedAt: getUnixtime(),
      }
      return existingJob.id
    }

    // 3. 기존 작업이 없는 경우 (Insert)
    else {
      const newJob: SyncJob<T> = {
        id: `job_${getRandomString(8, false)}`,
        status: JobStatus.PENDING,
        retryCount: 0,
        createdAt: getUnixtime(),
        updatedAt: getUnixtime(),
        ...jobDetails,
        localStringId: jobIdentifier,
      }
      console.info(
        `[SyncQueue] ➕ INSERT: Adding new job ${newJob.id} for ${jobIdentifier} (${tag})`,
      )
      this.queue.push(newJob)
      this.enforceQueueSizeLimit()
      this.notifyQueueChange()
      this.processQueue()
      return newJob.id
    }
  }

  /**
   * 큐 상태 변경 리스너를 등록합니다
   * @param listener 상태 변경 시 호출될 콜백 함수
   * @returns 리스너 제거 함수
   */
  public onQueueChange(listener: () => void): () => void {
    this.queueChangeListeners.push(listener)
    return () => {
      const index = this.queueChangeListeners.indexOf(listener)
      if (index > -1) {
        this.queueChangeListeners.splice(index, 1)
      }
    }
  }

  /**
   * 모든 리스너에게 큐 상태 변경을 알립니다
   */
  private notifyQueueChange(): void {
    this.queueChangeListeners.forEach(listener => {
      try {
        listener()
      } catch (error) {
        console.error('[SyncQueue] Listener error:', error)
      }
    })
  }

  private updateJobStatus(jobId: string, status: JobStatus, error?: string): void {
    const jobIndex = this.queue.findIndex(job => job.id === jobId)
    if (jobIndex !== -1) {
      this.queue[jobIndex].status = status
      this.queue[jobIndex].updatedAt = getUnixtime()
      if (error !== undefined) this.queue[jobIndex].error = error
      this.notifyQueueChange()
    }
  }

  /**
   * 같은 실행 순서 묶음(tags)에서 가장 최근 job이 아직 시작 전인 배치 job이면 그 data에 항목을 합친다.
   * 실행 중(PROCESSING)·재시도 중인 job에는 합치지 않는다 — 실행 중 끼어든 변경은 새 job이 맡는다.
   * 가장 최근 job일 때만 합치는 이유: 그 뒤에 쌓인 다른 job(삭제·복원 등)보다 먼저 실행되지 않도록.
   * @param tags 실행 순서를 함께 따질 태그들
   * @param localStringIdPrefix 합칠 배치 job의 localStringId 접두사
   * @param merge 기존 data를 받아 합친 data를 돌려준다 (null이면 합치지 않음)
   * @returns 합쳤으면 true
   */
  public mergeIntoLatestPendingBatch<T>(
    tags: string[],
    localStringIdPrefix: string,
    merge: (data: T) => T | null,
  ): boolean {
    let latest: SyncJob | undefined
    for (let i = this.queue.length - 1; i >= 0; i--) {
      const job = this.queue[i]
      if (job.tag && tags.includes(job.tag) && job.status !== JobStatus.COMPLETED) {
        latest = job
        break
      }
    }
    if (!latest || !latest.localStringId?.startsWith(localStringIdPrefix)) return false
    if (
      latest.status !== JobStatus.PENDING ||
      latest.retryCount > 0 ||
      latest.nextRetryAt !== undefined ||
      latest.error !== undefined
    ) {
      return false
    }
    const merged = merge(latest.data as T)
    if (!merged) return false
    latest.data = merged
    latest.updatedAt = getUnixtime()
    this.notifyQueueChange()
    return true
  }

  /**
   * 특정 localStringId를 가진 작업을 큐에서 제거합니다
   * @param localStringId - 제거할 작업의 localStringId
   */
  public removeJobByLocalStringId(localStringId: string): void {
    const initialLength = this.queue.length
    this.queue = this.queue.filter(job => job.localStringId !== localStringId)
    const removedCount = initialLength - this.queue.length

    // 대기 큐에서도 제거
    let pendingRemovedCount = 0
    if (this.pendingQueues.has(localStringId)) {
      pendingRemovedCount = this.pendingQueues.get(localStringId)!.length
      this.pendingQueues.delete(localStringId)
    }

    const totalRemovedCount = removedCount + pendingRemovedCount
    if (totalRemovedCount > 0) {
      console.info(
        `[SyncQueue] Removed ${totalRemovedCount} job(s) for localStringId: ${localStringId} (queue: ${removedCount}, pending: ${pendingRemovedCount})`,
      )
      this.notifyQueueChange()
    }
  }

  /**
   * 큐 처리 락 획득
   * @returns 락 획득 성공 여부
   */
  private async acquireProcessLock(): Promise<boolean> {
    if (this.processLock) {
      // 이미 처리 중이면 false 반환 (대기하지 않음)
      return false
    }
    this.processLock = new Promise<void>(resolve => {
      this.processLockResolve = resolve
    })
    return true
  }

  /**
   * 큐 처리 락 해제
   */
  private releaseProcessLock(): void {
    if (this.processLockResolve) {
      this.processLockResolve()
      this.processLock = null
      this.processLockResolve = null
    }
  }

  /**
   * 큐 처리를 일시정지합니다 (카메라 촬영 등 메모리 민감 구간).
   * 사진 업로드 job은 WebP 변환/base64 처리로 WebView 메모리를 크게 점유하므로,
   * 카메라 프리뷰와 동시에 돌면 저사양 기기에서 시스템이 앱을 강제 종료할 수 있습니다.
   * 이미 실행 중인 job은 완료까지 진행되고, 다음 job부터 멈춥니다.
   * pause/resume은 쌍으로 호출해야 하며, resume 유실 시 retry sweep이 10분 후 강제 재개합니다.
   */
  public pause(): void {
    if (this.pauseCount === 0) {
      this.pausedAt = Date.now()
      console.info('[SyncQueue] ⏸️ 큐 처리 일시정지 (메모리 민감 구간)')
    }
    this.pauseCount++
  }

  // 일시정지를 해제하고, 모든 pause가 해제되면 대기 중인 작업 처리를 재개합니다
  public resume(): void {
    if (this.pauseCount === 0) return
    this.pauseCount--
    if (this.pauseCount === 0) {
      console.info('[SyncQueue] ▶️ 큐 처리 재개')
      this.processQueue()
    }
  }

  public async processQueue(): Promise<void> {
    if (this.pauseCount > 0) return
    // 매뉴얼 데모 API 핸들러 설치 중에는 업로드가 데모 응답으로 삼켜지므로 정지
    if (isSyncSuspended()) return
    if (!this.isNetworkConnected) return

    // 락 획득 시도 (이미 처리 중이면 건너뜀)
    const acquired = await this.acquireProcessLock()
    if (!acquired) return

    this.isProcessing = true

    try {
      const jobToProcess = this.queue
        .filter(job => job.status === JobStatus.PENDING)
        .sort((a, b) => a.priority - b.priority || a.createdAt - b.createdAt)[0]

      if (jobToProcess) {
        await this.processJob(jobToProcess)
        this.isProcessing = false
        this.releaseProcessLock()
        // 재귀 호출 대신 setImmediate 패턴으로 스택 오버플로우 방지
        setTimeout(() => this.processQueue(), 0)
      } else {
        this.isProcessing = false
        this.releaseProcessLock()
      }
    } catch (error) {
      console.error('[SyncQueue] processQueue error:', error)
      this.isProcessing = false
      this.releaseProcessLock()
    }
  }

  /**
   * 작업의 고유 signature를 생성합니다 (중복 실행 방지용)
   * @param data - 작업 데이터
   * @param tag - 작업 태그
   * @returns 작업 signature
   */
  private generateExecutionSignature(data: any, tag?: string): string {
    const id = String(data?.id || '')
    const localStringId = String(data?.localStringId || '')
    const deleted_at = data?.deleted_at || null

    // 작업 유형 판별
    let actionType = 'UNKNOWN'
    if (id.includes('_need_to_upload')) {
      actionType = 'CREATE'
    } else if (deleted_at) {
      actionType = 'DELETE'
    } else {
      actionType = 'UPDATE'
    }

    // docs의 경우 items의 변경을 반영하기 위해 updated_at 포함
    // docs는 같은 레코드를 여러 번 수정할 수 있으므로 변경 시점을 signature에 포함
    if (tag === 'docs' && data.updated_at) {
      return `${tag}_${localStringId}_${actionType}_${id}_${data.updated_at}`
    }

    // defects의 경우 undo/redo로 데이터가 변경될 수 있으므로
    // fields_updated_at의 타임스탬프를 signature에 포함하여 다른 작업으로 인식
    if (tag === 'defects' && data.fields_updated_at) {
      // fields_updated_at는 객체이므로 JSON 문자열화하여 해시 생성
      const fieldsHash = JSON.stringify(data.fields_updated_at)
      return `${tag}_${localStringId}_${actionType}_${id}_${fieldsHash}`
    }

    // signature: tag_localStringId_actionType_id
    return `${tag}_${localStringId}_${actionType}_${id}`
  }

  /**
   * SQLite에서 최신 데이터를 조회합니다
   * @param localStringId - 조회할 항목의 localStringId
   * @param tag - 작업 태그 (shape, defectPhoto 등)
   * @returns 최신 데이터 또는 null
   */
  private async getLatestDataFromDB(
    localStringId: string,
    tag?: string,
  ): Promise<
    | Defect
    | DefectPhoto
    | FacilityPhoto
    | Floorplan
    | UserExportReportRecord
    | QualityInspection
    | QualityInspectionResult
    | QualityResultPhoto
    | null
  > {
    try {
      const db = await SQL.getDatabase()
      let tableName = ''

      // 태그에 따라 테이블명 결정
      switch (tag) {
        case 'defects':
          tableName = 'defects'
          break
        case 'defectPhoto':
          tableName = 'defect_photos'
          break
        case 'facilityPhoto':
          tableName = 'facility_photos'
          break
        case 'floorplans':
          tableName = 'floorplans'
          break
        case 'log':
          tableName = 'device_event_log'
          break
        case 'docs':
          tableName = 'docs'
          break
        case 'voiceFile':
          tableName = 'voice_files'
          break
        case 'qualityInspection':
          tableName = 'quality_inspections'
          break
        case 'qualityResult':
          tableName = 'quality_inspection_results'
          break
        case 'qualityResultPhoto':
          tableName = 'quality_result_photos'
          break
        default:
          console.warn(`[SyncQueue] Unknown tag: ${tag}. Cannot fetch latest data.`)
          return null
      }

      console.info(`[SyncQueue] 🔍 Fetching latest data from ${tableName} for ${localStringId}`)

      const result = await db.query(`SELECT * FROM ${tableName} WHERE localStringId = ?`, [
        localStringId,
      ])

      if (result.values && result.values.length > 0) {
        const parsed = parseRow(result.values[0])
        console.info(
          `[SyncQueue] 📖 Found data: id=${parsed.id}, modifiedFromLocal=${parsed.modifiedFromLocal}`,
        )
        return parsed
      }

      console.warn(`[SyncQueue] ⚠️  No data found in ${tableName} for ${localStringId}`)
      return null
    } catch (error) {
      console.error(
        `[SyncQueue] ❌ Error fetching latest data for ${localStringId} (tag: ${tag}):`,
        error,
      )
      return null
    }
  }

  /**
   * pendingQueues에 있는 작업을 메인 큐로 이동합니다
   * 기존에 같은 localStringId를 가진 작업중 실패한 작업이 있다면 제거하고 최신 작업으로 교체합니다
   * @param localStringId - 처리할 localStringId
   */
  private processPendingJobs(localStringId: string): void {
    if (this.pendingQueues.has(localStringId)) {
      const pendingJobs = this.pendingQueues.get(localStringId)!
      this.pendingQueues.delete(localStringId)

      console.info(
        `[SyncQueue] Moving ${pendingJobs.length} pending job(s) for ${localStringId} to main queue`,
      )

      // 기존 큐에서 같은 localStringId를 가진 모든 작업 제거
      const removedJobs = this.queue.filter(
        job => job.localStringId === localStringId && job.status === JobStatus.FAILED,
      )
      if (removedJobs.length > 0) {
        console.info(
          `[SyncQueue] Removing ${removedJobs.length} existing job(s) for ${localStringId} (statuses: ${removedJobs.map(j => j.status).join(', ')})`,
        )
        this.queue = this.queue.filter(job => job.localStringId !== localStringId)
      }

      // pendingQueue의 작업들을 메인 큐에 추가
      for (const pendingJob of pendingJobs) {
        this.queue.push(pendingJob)
      }

      this.notifyQueueChange()
      this.processQueue()
    }
  }

  private async processJob(job: SyncJob): Promise<void> {
    this.updateJobStatus(job.id, JobStatus.PROCESSING)

    console.info(`[SyncQueue] 🔄 Processing job ${job.id} (${job.localStringId}, tag: ${job.tag})`)

    try {
      // 배치 작업(-batch 접미사)은 getLatestDataFromDB를 건너뛰고 바로 실행
      if (job.tag?.endsWith('-batch')) {
        console.info(
          `[SyncQueue] Batch job ${job.id} (${job.tag}) - processing ${Array.isArray(job.data) ? job.data.length : 0} items`,
        )
        console.info(`[SyncQueue] Executing batch job ${job.id}`)
        const result = await job.execute(job.data)
        console.info(`[SyncQueue] Execute completed for batch job ${job.id}`)
        await job.onSuccess(result, job.data)

        this.updateJobStatus(job.id, JobStatus.COMPLETED)
        this.queue = this.queue.filter(j => j.id !== job.id)
        this.notifyQueueChange()
        return
      }

      // 일반 작업: SQLite에서 최신 데이터 조회
      if (job.localStringId && job.tag) {
        const latestData = await this.getLatestDataFromDB(job.localStringId, job.tag)

        if (latestData) {
          // modifiedFromLocal이 false면 이미 동기화 완료된 것
          if (!latestData.modifiedFromLocal) {
            console.warn(
              `[SyncQueue] SKIP: Job ${job.id} (${job.localStringId}) is already synced (modifiedFromLocal=false).`,
            )

            this.updateJobStatus(job.id, JobStatus.COMPLETED)
            this.queue = this.queue.filter(j => j.id !== job.id)
            this.notifyQueueChange()
            // pendingQueues에 대기 중인 작업이 있으면 처리
            this.processPendingJobs(job.localStringId)
            return
          }

          // 최신 데이터로 job.data 업데이트 (pendingQueues에서 온 과거 데이터 방지)
          const oldId = job.data?.id
          const newId = latestData?.id
          if (oldId !== newId) {
            console.info(
              `[SyncQueue] Updating job ${job.id} data: id changed from ${oldId} to ${newId}`,
            )
          }
          job.data = latestData
        } else {
          // SQLite에서 데이터를 찾을 수 없는 경우 (삭제되었을 가능성)
          console.warn(
            `[SyncQueue] ⚠️  SKIP: Job ${job.id} (${job.localStringId}) data not found in SQLite. Item may have been deleted.`,
          )
          this.updateJobStatus(job.id, JobStatus.COMPLETED)
          this.queue = this.queue.filter(j => j.id !== job.id)
          this.notifyQueueChange()

          // pendingQueues에 대기 중인 작업이 있으면 처리
          this.processPendingJobs(job.localStringId)
          return
        }
      }

      console.info(`[SyncQueue] ▶️  Executing job ${job.id} for ${job.localStringId}`)
      // 생성·복원 job은 성공 즉시 병합 유예 기준 시각을 남긴다 (onIdMap·onSuccess보다 먼저 —
      // 그 사이 도착한 병합이 이미 시작된 GET 응답만 보고 방금 올린 항목을 지우지 않도록)
      const graceKey = getUploadGraceKey(job.data)
      const result = await job.execute(job.data)
      console.info(`[SyncQueue] Execute completed for job ${job.id}`)
      if (graceKey) Merge.markRecentlyUploaded(graceKey)

      // onIdMap을 onSuccess보다 먼저 실행 (ID 매핑을 먼저 완료하여 race condition 방지)
      if (job.localStringId && job.onIdMap) {
        try {
          await job.onIdMap(job.localStringId, result)
          console.info(`[SyncQueue] 🆔 ID mapping completed for ${job.localStringId}`)
        } catch (onIdMapError) {
          // onIdMap 실패는 로그만 남기고 job은 성공으로 처리
          // SQLite는 이미 execute에서 업데이트되었으므로 핵심 데이터는 안전함
          console.error(
            `[SyncQueue] onIdMap failed for job ${job.id}, but job will be marked as completed.`,
            onIdMapError,
          )
          console.warn(
            `[SyncQueue] Memory may be out of sync for ${job.localStringId}. Will be fixed on next app restart.`,
          )
        }
      }

      await job.onSuccess(result, job.data)

      this.updateJobStatus(job.id, JobStatus.COMPLETED)
      this.queue = this.queue.filter(j => j.id !== job.id)

      this.notifyQueueChange()

      // 대기 중인 작업이 있으면 처리
      if (job.localStringId) {
        this.processPendingJobs(job.localStringId)
      }

      if (job.tag === 'defects') {
        console.info(
          `[SyncQueue] Shape ${job.localStringId} completed. Checking for dependent jobs...`,
        )
        // DependencyNotReadyError로 실패한 작업들을 찾아서 재시도
        const dependentJobs = this.queue.filter(
          j =>
            j.status === JobStatus.PENDING &&
            j.error?.includes('Dependent shape') &&
            (j.tag === 'defectPhoto' || j.tag === 'facilityPhoto'),
        )
        if (dependentJobs.length > 0) {
          console.info(
            `[SyncQueue] Found ${dependentJobs.length} dependent jobs waiting for shape. Triggering retry...`,
          )
          // 즉시 큐 재처리 trigger
          setTimeout(() => this.processQueue(), 500)
        }
      }
    } catch (error) {
      console.error(`[SyncQueue] Error processing job ${job.id}:`, error)
      // 품질점검 job의 403(권한: 부작업자가 타인 행 수정, 검수완료 잠금)은 재시도 무의미 -
      // 404/409와 동일하게 onError로 위임 후 큐에서 정리 (기존 태그의 403 재시도 동작은 유지)
      const isQualityForbidden =
        error instanceof ApiError &&
        error.statusCode === 403 &&
        !!job.tag &&
        job.tag.startsWith('quality')
      // 결함 job의 서버 검증 거절(400/422)도 재시도 무의미 - onError가 자동 수리 또는 행 거절 표식을 남긴다
      // (그 전엔 maxRetries 후 backoff로 끝없이 재시도해 "저장 중"이 사라지지 않았다)
      const isDefectRejected =
        error instanceof ApiError &&
        isValidationRejectStatus(error.statusCode) &&
        (job.tag === 'defects' || job.tag === 'defects-batch')
      if (
        (error instanceof ApiError && (error.statusCode === 404 || error.statusCode === 409)) ||
        isQualityForbidden ||
        isDefectRejected
      ) {
        await job.onError(error, job.data, (error as ApiError).statusCode)
        // onError에서 자체 제거하지 않은 job을 큐에서 정리
        this.updateJobStatus(job.id, JobStatus.COMPLETED)
        this.queue = this.queue.filter(j => j.id !== job.id)
        this.notifyQueueChange()
        // 자동 수리가 다시 넣은 job(실행 중이라 대기 큐로 감)을 이어서 처리
        if (isDefectRejected && job.localStringId) this.processPendingJobs(job.localStringId)
        return
      }
      if (error instanceof DependencyRejectedError) {
        // 부모 결함이 서버 거절로 멈춤: 3초 재시도 루프 대신 job을 끝낸다 (행 mfl=1은 유지 — 서버에 없다는 사실 그대로).
        // 부모가 다시 저장돼 생성되면 부모 onIdMap이 이 행을 다시 큐에 넣는다.
        console.warn(`[SyncQueue] Job ${job.id} (${job.localStringId}) 중단: ${error.message}`)
        this.updateJobStatus(job.id, JobStatus.COMPLETED)
        this.queue = this.queue.filter(j => j.id !== job.id)
        this.notifyQueueChange()
        if (job.localStringId) this.processPendingJobs(job.localStringId)
        return
      }
      if (error instanceof DependencyNotReadyError) {
        console.warn(
          `[SyncQueue] Job ${job.id} (${job.localStringId}) failed due to dependency issue. Will retry after 3 seconds...`,
        )
        // 의존성 미해결: 3초 후 재시도
        setTimeout(() => {
          this.updateJobStatus(job.id, JobStatus.PENDING, error.message)
          this.processQueue()
        }, 3000)
        return
      }
      job.retryCount++
      await job.onError(error, job.data)
      if (job.retryCount >= job.maxRetries) {
        // maxRetries 초과 시에도 큐에서 제거하지 않고 exponential backoff로 재시도
        const backoffDelay = Math.min(
          Math.pow(2, Math.min(job.retryCount - job.maxRetries, 10)) * 5000, // 5초부터 시작해서 최대 약 85분
          5 * 60 * 1000, // 최대 5분 간격
        )

        console.warn(
          `[SyncQueue] Job ${job.id} exceeded maxRetries (${job.maxRetries}). Retrying with backoff delay: ${backoffDelay}ms`,
        )

        // 대기 중인 작업이 있으면 처리 (실패한 작업은 큐에서 제거하지 않음)
        // 중요: processPendingJobs를 먼저 호출하여 대기 중인 작업을 처리
        // 이렇게 하면 현재 실패한 job과 pending job 중 하나만 큐에 남게 됨
        if (job.localStringId && this.pendingQueues.has(job.localStringId)) {
          console.info(
            `[SyncQueue] Processing pending jobs for ${job.localStringId} before retry delay`,
          )

          // pending job이 있으면 현재 실패한 job을 FAILED로 마킹
          this.updateJobStatus(
            job.id,
            JobStatus.FAILED,
            `Exceeded maxRetries. Pending jobs will take over. Last error: ${error instanceof Error ? error.message : String(error)}`,
          )

          // pending jobs를 메인 큐로 이동 (중복 체크 로직이 적용됨)
          this.processPendingJobs(job.localStringId)
        } else {
          // pending job이 없으면 nextRetryAt 설정 후 FAILED 마킹 (retry sweep이 처리)
          job.nextRetryAt = Date.now() + backoffDelay
          this.updateJobStatus(
            job.id,
            JobStatus.FAILED,
            `Retry attempt ${job.retryCount} after ${backoffDelay}ms delay: ${error instanceof Error ? error.message : String(error)}`,
          )
        }
      } else {
        this.updateJobStatus(
          job.id,
          JobStatus.PENDING,
          error instanceof Error ? error.message : String(error),
        )
      }
    }
  }

  // 배치 job의 data(배열) 내 taskId 매칭 아이템 수를 반환 (일반 job은 0 또는 1)
  private getJobItemCount(job: SyncJob, taskId: string): number {
    if (Array.isArray(job.data)) {
      return job.data.filter((item: any) => item && String(item.task_id) === taskId).length
    }
    const jobData = job.data as any
    return jobData && String(jobData.task_id) === taskId ? 1 : 0
  }

  private getJobCountByStatus(taskId: string, tag: string, statuses: JobStatus[]): number {
    if (!taskId) return 0
    let count = 0
    for (const job of this.queue) {
      if (job.tag === 'log' || job.tag !== tag) continue
      if (!statuses.includes(job.status)) continue
      count += this.getJobItemCount(job, taskId)
    }
    return count
  }

  /**
   * 특정 taskId에 대해 "진행 중인" (PENDING 또는 PROCESSING) 작업의 개수를 반환합니다.
   * 배치 작업은 내부 아이템 수만큼 집계됩니다.
   * (DeviceEventLog 작업은 제외됩니다.)
   */
  public getInProgressJobCountByTaskId(taskId: string): number {
    if (!taskId) return 0
    let count = 0
    for (const job of this.queue) {
      if (job.tag === 'log' || job.tag === 'log-batch') continue
      // PENDING, PROCESSING, 재시도 가능한 FAILED (maxRetries 미초과)
      const matchesStatus =
        job.status === JobStatus.PENDING ||
        job.status === JobStatus.PROCESSING ||
        (job.status === JobStatus.FAILED && job.retryCount < job.maxRetries)
      if (!matchesStatus) continue
      count += this.getJobItemCount(job, taskId)
    }
    return count
  }

  //특정 tag에 진행중인 작업의 개수를 반환합니다.
  public getInProgressJobCountByTag(taskId: string, tag: string): number {
    return this.getJobCountByStatus(taskId, tag, [JobStatus.PENDING, JobStatus.PROCESSING])
  }

  // 해당 tag의 미완료(PENDING/PROCESSING/재시도 대기 FAILED) 작업 존재 여부 - 중복 적재 방지용
  // FAILED라도 nextRetryAt이 있으면 retry sweep이 PENDING으로 되살릴 예정이므로 활성으로 간주
  public hasActiveJobByTag(tag: string): boolean {
    return this.queue.some(
      job =>
        job.tag === tag &&
        (job.status === JobStatus.PENDING ||
          job.status === JobStatus.PROCESSING ||
          (job.status === JobStatus.FAILED &&
            (job.retryCount < job.maxRetries || job.nextRetryAt !== undefined))),
    )
  }

  /**
   * 특정 taskId에 대해 "실패한" (FAILED + maxRetries 초과) 작업의 개수를 반환합니다.
   * 배치 작업은 내부 아이템 수만큼 집계됩니다.
   * (DeviceEventLog 작업은 제외됩니다.)
   */
  public getFailedJobCountByTaskId(taskId: string): number {
    if (!taskId) return 0
    let count = 0
    for (const job of this.queue) {
      if (job.tag === 'log' || job.tag === 'log-batch') continue
      if (job.status !== JobStatus.FAILED || job.retryCount < job.maxRetries) continue
      count += this.getJobItemCount(job, taskId)
    }
    return count
  }

  /**
   * 특정 taskId에 대해 UI에 표시해야 할 전체 미동기화 작업 수를 반환합니다.
   * 진행 중(PENDING/PROCESSING) + 장기 실패(FAILED + maxRetries 초과)
   * (DeviceEventLog 작업은 제외됩니다.)
   */
  public getTotalUnsyncedJobCountByTaskId(taskId: string): number {
    if (!taskId) return 0
    let count = 0
    for (const job of this.queue) {
      if (job.tag === 'log' || job.tag === 'log-batch') continue
      if (job.status === JobStatus.COMPLETED) continue
      const itemCount = this.getJobItemCount(job, taskId)
      if (itemCount === 0) continue
      // PENDING, PROCESSING, FAILED(재시도 가능 + 장기 실패) 모두 포함
      count += itemCount
    }
    return count
  }

  /**
   * 현재 큐에 있는 모든 작업의 복사본을 반환합니다. (디버깅 및 고급 UI용)
   * @returns 현재 큐에 있는 모든 작업의 배열
   */
  public getQueueState(): Readonly<SyncJob[]> {
    return this.queue
  }

  public clearQueue(): void {
    this.queue = []
    this.pendingQueues.clear()
    this.isProcessing = false
    // 락 해제
    this.releaseProcessLock()
    // retry sweep 타이머 정리 후 재시작
    if (this.retrySweepTimer) {
      clearInterval(this.retrySweepTimer)
      this.retrySweepTimer = null
    }
    this.startRetrySweep()
  }

  /**
   * maxRetries를 초과한 장기 실패 작업들의 상세 정보를 반환합니다. (디버깅용)
   * @param taskId 특정 작업 ID (선택사항)
   * @returns 장기 실패 중인 작업들의 배열
   */
  public getLongFailedJobs(taskId?: string): Readonly<SyncJob[]> {
    return this.queue.filter(job => {
      // maxRetries를 초과한 작업들만
      if (job.retryCount < job.maxRetries) return false

      // log 태그 제외
      if (job.tag === 'log') return false

      // taskId가 지정된 경우 해당 작업만
      if (taskId) {
        const jobData = job.data as any
        return jobData && String(jobData.task_id) === taskId
      }

      return true
    })
  }

  // 여러 localStringId를 한 번에 조회 (배치용)
  public async findIdsBatch(
    localStringIds: string[],
    table: 'defects' | 'defect_photos',
  ): Promise<Map<string, number | string>> {
    const result = new Map<string, number | string>()
    if (localStringIds.length === 0) return result

    const db = await SQL.getDatabase()
    const unique = [...new Set(localStringIds)]
    // 원본 + _need_to_upload 제거 버전 모두 조회
    const allIds = unique.flatMap(id => [id, id.replace('_need_to_upload', '')])
    const placeholders = allIds.map(() => '?').join(',')
    const rows = await db.query(
      `SELECT id, localStringId, deleted_at FROM ${table} WHERE localStringId IN (${placeholders}) OR id IN (${placeholders})`,
      [...allIds, ...allIds],
    )

    // localStringId → {id, deleted_at} 매핑 구축
    const dbMap = new Map<string, { id: number | string; deleted_at: string | null }>()
    if (rows?.values) {
      for (const row of rows.values) {
        if (row.localStringId) dbMap.set(String(row.localStringId), row)
        dbMap.set(String(row.id), row)
      }
    }

    for (const localId of unique) {
      const stripped = localId.replace('_need_to_upload', '')
      const found = dbMap.get(localId) || dbMap.get(stripped)
      if (found?.deleted_at) {
        result.set(localId, 'is_deleted')
      } else if (found) {
        result.set(localId, found.id)
      } else {
        result.set(localId, '_need_to_upload')
      }
    }
    return result
  }

  public async findShapeId(shapeLocalDatabaseId: string): Promise<number | string> {
    const db = await SQL.getDatabase()
    const shape = await db.query(
      'SELECT id, deleted_at FROM defects WHERE localStringId = ? OR id = ?',
      [shapeLocalDatabaseId, shapeLocalDatabaseId],
    )
    const secondCheck = await db.query(
      'SELECT id, deleted_at FROM defects WHERE localStringId = ? OR id = ?',
      [
        shapeLocalDatabaseId.replace('_need_to_upload', ''),
        shapeLocalDatabaseId.replace('_need_to_upload', ''),
      ],
    )
    if (
      (shape && shape.values?.[0]?.deleted_at) ||
      (secondCheck && secondCheck.values?.[0]?.deleted_at)
    ) {
      return 'is_deleted'
    }
    if (!shape && !secondCheck) return '_need_to_upload'
    return shape.values?.[0]?.id ?? secondCheck.values?.[0]?.id ?? '_need_to_upload'
  }

  /**
   * 서버 미생성 결함이 서버 검증 거절 표식(유예 중)으로 멈춰 있는지 — 그 결함에 의존하는 사진·자식 결함 job을 끝낼지 판단
   * @param shapeLocalStringId 부모 결함의 localStringId
   */
  public async isDependencyRejected(shapeLocalStringId: string): Promise<boolean> {
    const db = await SQL.getDatabase()
    const result = await db.query(
      'SELECT id, fields_updated_at FROM defects WHERE localStringId = ? AND modifiedFromLocal = 1',
      [shapeLocalStringId],
    )
    const row = result.values?.[0]
    if (!row || !String(row.id).includes('_need_to_upload')) return false
    const rejected = isSyncRejectedActive(row)
    if (rejected) this.markDependencyBlocked(shapeLocalStringId)
    return rejected
  }

  /**
   * 부모 거절로 사진·자식 결함 job을 끝내거나 만들지 않은 부모를 기억한다 (부모 생성 시 재투입 대상).
   * 메모리 전용 — 앱 재시작 뒤에는 주기 동기화가 미동기화 행을 다시 큐에 넣는다.
   * @param shapeLocalStringId 부모 결함의 localStringId
   */
  public markDependencyBlocked(shapeLocalStringId: string): void {
    this.blockedDependencies.add(shapeLocalStringId)
  }

  /**
   * 부모가 막힌 적이 있으면 기록을 지우고 true (재투입이 필요한지)
   * @param shapeLocalStringId 생성된 부모 결함의 localStringId
   */
  public takeDependencyBlocked(shapeLocalStringId: string): boolean {
    return this.blockedDependencies.delete(shapeLocalStringId)
  }

  public async findDefectPhotoId(defectPhotoLocalDatabaseId: string): Promise<number | string> {
    const db = await SQL.getDatabase()
    const defectPhoto = await db.query(
      'SELECT id, deleted_at FROM defect_photos WHERE localStringId = ? OR id = ?',
      [defectPhotoLocalDatabaseId, defectPhotoLocalDatabaseId],
    )

    if (defectPhoto && defectPhoto.values?.[0]?.deleted_at) {
      return 'is_deleted'
    }
    if (!defectPhoto) return '_need_to_upload'
    return defectPhoto.values?.[0]?.id ?? '_need_to_upload'
  }
}

export const syncQueue = SyncQueue.getInstance()
